import React, { useCallback, useMemo, useState } from 'react';
import { TypographyVariant } from '@mui/material/styles';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import {
  applyScaleBuildingToGfa,
  type ScaleBuildingToGfaStatus,
} from '@shared/helpers/design/design.helpers';
import { useDesignGfa } from '../hooks/storeys.hooks';
import { BUILDING_GFA_OVERLAP_WARNING } from '../helpers/building-overlap.constants';
import { useUpdateBuildingDesign } from '../hooks/design.hooks';
import { isDesignSnapshotSupersededError } from '../helpers/design-snapshot-persist.helpers';
import { getWritableBuildingId } from '../store/design-store.hooks';
import amplitudeLog from '@client/amplitude';
import { AMP_DESIGN_MANUAL_GFA_UPDATE } from '@shared/constants';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { DesignAreaField } from '../components/DesignAreaField';
import { isEmptyBuildingGeometry } from '@shared/helpers/design/storey.helpers';
import {
  useBuildingCandidate,
  useBuildingCandidateStoreyCount,
  useBuildingCandidateTargetGfa,
  useSelectedBuildingId,
  useSetBuildingCandidateTargetGfa,
} from '../store/design-store.hooks';
import { DEFAULT_NEW_BUILDING_STOREY_COUNT } from '../store/design.store';

const GFA_INVALID_MESSAGE = 'Enter a valid gross floor area';
const GFA_TOO_LARGE_MESSAGE = 'Gross floor area must not exceed 100 000 m²';
const GFA_REJECTED_MESSAGE = 'Could not apply that gross floor area';
const MIN_GFA_VALUE = 1;
const MAX_GFA_VALUE = 100_000;
const EDITABLE_NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  useGrouping: false,
  maximumFractionDigits: 20,
});

const formatEditableNumber = (value?: number): string => {
  if (value === undefined || Number.isNaN(value)) return '';
  return EDITABLE_NUMBER_FORMATTER.format(value);
};

const clampGfaValue = (value?: number): number | undefined => {
  if (value === undefined || Number.isNaN(value)) return undefined;
  return Math.max(MIN_GFA_VALUE, value);
};

const sanitizeDecimalInput = (raw: string): string => {
  const cleaned = raw.replace(/[^\d.,]/g, '').replace(/,/g, '.');
  const [head = '', ...rest] = cleaned.split('.');
  // 100,000 is six digits. A seventh digit is always over the limit.
  const digits = head.slice(0, 6);
  const text = rest.length ? `${digits}.${rest.join('')}` : digits;
  const parsed = Number(text);
  if (!text || !Number.isFinite(parsed) || parsed <= MAX_GFA_VALUE) return text;
  const shorter = digits.slice(0, -1);
  return rest.length ? `${shorter}.${rest.join('')}` : shorter;
};

const parseDecimalInput = (raw: string): number | undefined => {
  const normalized = raw.replace(/,/g, '.').trim();
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

type BuildingGfaInputProps = {
  variant?: TypographyVariant;
  /** Mount directly in the same selected editing state as after a user click. */
  initiallyEditing?: boolean;
};

export const BuildingGfaInput: React.FC<BuildingGfaInputProps> = ({
  variant = 'h6',
  initiallyEditing = false,
}) => {
  const designGfa = useDesignGfa();
  const buildingCandidate = useBuildingCandidate();
  const candidateStoreyCount =
    useBuildingCandidateStoreyCount() ?? DEFAULT_NEW_BUILDING_STOREY_COUNT;
  const candidateTargetGfa = useBuildingCandidateTargetGfa();
  const selectedBuildingId = useSelectedBuildingId();
  const setBuildingCandidateTargetGfa = useSetBuildingCandidateTargetGfa();
  const isEmptySelectedCandidate = Boolean(
    buildingCandidate &&
    buildingCandidate.id === selectedBuildingId &&
    isEmptyBuildingGeometry(buildingCandidate),
  );
  const displayedDesignGfa = isEmptySelectedCandidate
    ? candidateTargetGfa
    : designGfa;
  const roundedDesignGfa = useMemo(() => {
    if (
      displayedDesignGfa === undefined ||
      !Number.isFinite(displayedDesignGfa)
    ) {
      return undefined;
    }
    return Math.round(displayedDesignGfa);
  }, [displayedDesignGfa]);
  const updateBuildingDesign = useUpdateBuildingDesign();
  const { enqueueSnackbar } = useSnackbar();
  const [isEditingGfa, startEditingGfa, stopEditingGfa] =
    useBooleanState(initiallyEditing);
  const [reselectToken, setReselectToken] = useState(0);
  const keepEditingSelection = useCallback(() => {
    setReselectToken((token) => token + 1);
  }, []);

  const gfaValue = useMemo(() => {
    if (roundedDesignGfa === undefined) return '';
    const roundedValue = roundedDesignGfa;
    if (isEditingGfa) {
      return formatEditableNumber(roundedValue);
    }
    return getDisplayNumber(roundedValue);
  }, [isEditingGfa, roundedDesignGfa]);

  const gfaPlaceholder = useMemo(() => {
    if (isEmptySelectedCandidate && candidateTargetGfa === undefined) {
      return getDisplayNumber(candidateStoreyCount * 1_000);
    }
    if (roundedDesignGfa === undefined) return '';
    return getDisplayNumber(roundedDesignGfa);
  }, [
    candidateStoreyCount,
    candidateTargetGfa,
    isEmptySelectedCandidate,
    roundedDesignGfa,
  ]);

  const gfaFillValue = useMemo(() => {
    if (isEmptySelectedCandidate && candidateTargetGfa === undefined) {
      return formatEditableNumber(candidateStoreyCount * 1_000);
    }
    return undefined;
  }, [candidateStoreyCount, candidateTargetGfa, isEmptySelectedCandidate]);

  const handleGfaClick = useCallback(() => {
    if (isEditingGfa) return;
    startEditingGfa();
  }, [isEditingGfa, startEditingGfa]);

  /**
   * Persist a GFA scale. Overlap is a no-op: no PUT, no Amplitude, field stays open.
   * Re-reads the latest design inside the persist queue so a concurrent storey
   * write is not replaced by a stale snapshot of sibling buildings.
   */
  const scaleDesignToGfa = useCallback(
    async (target?: number): Promise<ScaleBuildingToGfaStatus> => {
      if (!target) return 'unchanged';
      const buildingId = getWritableBuildingId();
      let status: ScaleBuildingToGfaStatus = 'unchanged';
      await updateBuildingDesign((latestDesign) => {
        const { design: next, status: nextStatus } = applyScaleBuildingToGfa(
          latestDesign,
          target,
          buildingId ?? latestDesign.buildings[0]?.id,
        );
        status = nextStatus;
        if (nextStatus !== 'applied') return latestDesign;
        return next;
      });
      return status;
    },
    [updateBuildingDesign],
  );

  /**
   * Persists a new GFA value if it differs from what is currently displayed.
   */
  const handleSaveGfa = useCallback(
    (value: string) => {
      const parsed = parseDecimalInput(value);
      const nextValue = clampGfaValue(parsed);
      if (!nextValue) {
        if (value.trim()) {
          enqueueSnackbar(GFA_INVALID_MESSAGE, { variant: 'error' });
          keepEditingSelection();
          return;
        }
        stopEditingGfa();
        return;
      }
      if (nextValue > MAX_GFA_VALUE) {
        enqueueSnackbar(GFA_TOO_LARGE_MESSAGE, { variant: 'error' });
        keepEditingSelection();
        return;
      }
      if (isEmptySelectedCandidate) {
        setBuildingCandidateTargetGfa(nextValue);
        amplitudeLog(AMP_DESIGN_MANUAL_GFA_UPDATE, { Value: nextValue });
        stopEditingGfa();
        return;
      }
      // Compare against what the user sees to avoid unnecessary updates.
      const baselineForComparison =
        roundedDesignGfa ??
        (Number.isFinite(displayedDesignGfa) ? displayedDesignGfa : undefined);

      if (
        baselineForComparison !== undefined &&
        Math.abs(nextValue - baselineForComparison) < 1
      ) {
        stopEditingGfa();
        return;
      }
      void (async () => {
        try {
          const result = await scaleDesignToGfa(nextValue);
          if (result === 'applied') {
            amplitudeLog(AMP_DESIGN_MANUAL_GFA_UPDATE, { Value: nextValue });
            stopEditingGfa();
            return;
          }
          if (result === 'overlap') {
            enqueueSnackbar(BUILDING_GFA_OVERLAP_WARNING, {
              variant: 'warning',
            });
          } else {
            enqueueSnackbar(GFA_REJECTED_MESSAGE, { variant: 'warning' });
          }
          keepEditingSelection();
        } catch (error: unknown) {
          if (isDesignSnapshotSupersededError(error)) {
            stopEditingGfa();
            return;
          }
          throw error;
        }
      })();
    },
    [
      displayedDesignGfa,
      enqueueSnackbar,
      isEmptySelectedCandidate,
      keepEditingSelection,
      roundedDesignGfa,
      scaleDesignToGfa,
      setBuildingCandidateTargetGfa,
      stopEditingGfa,
    ],
  );

  const handleCancelGfa = useCallback(() => {
    stopEditingGfa();
  }, [stopEditingGfa]);

  const formatGfaInput = useCallback(
    (raw: string) => sanitizeDecimalInput(raw),
    [],
  );

  return (
    <DesignAreaField
      label="Gross Floor Area"
      variant={variant}
      value={gfaValue}
      defaultValue={gfaPlaceholder}
      fillValue={gfaFillValue}
      editing={isEditingGfa}
      selectOnFocus
      reselectToken={reselectToken}
      onClick={handleGfaClick}
      onSave={handleSaveGfa}
      onCancel={handleCancelGfa}
      formatInput={formatGfaInput}
    />
  );
};

export default BuildingGfaInput;
